import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Clock, FileText, Stethoscope, Scale, Send, Handshake, Gavel, ArrowRight, ExternalLink } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const HowLongDoesSettlementTakePage = () => {
  const faqData = [
    {
      question: "What's the fastest a car accident case can settle?",
      answer: "Simple cases with clear liability and minor injuries can settle in a few months. However, I generally advise against rushing—settling too fast often means leaving money on the table."
    },
    {
      question: "What's the longest a car accident case can take?",
      answer: "Complex cases that go to trial can take 2-3 years or more. Most cases settle before trial, but having an attorney willing to go to trial often results in better settlement offers."
    },
    {
      question: "Why do insurance companies delay settlements?",
      answer: "Delay is a strategy. Insurance companies know you have bills to pay. They hope financial pressure will make you accept a lower offer. Having an attorney signals you can wait for a fair offer."
    },
    {
      question: "Can I get money while my case is pending?",
      answer: "Your own auto insurance (PIP coverage) may pay for immediate medical expenses. If you have health insurance, use it for treatment. The settlement comes later to compensate for all damages."
    },
    {
      question: "Does hiring a lawyer make the process take longer?",
      answer: "Sometimes, but for good reason. Attorneys typically wait until you've reached maximum medical improvement to ensure full compensation. Faster isn't always better if it means accepting less than you deserve."
    }
  ];

  const phases = [
    {
      number: 1,
      title: "Medical Treatment",
      duration: "Weeks to months",
      description: "You focus on getting better. Follow your doctor's treatment plan. Your case value depends largely on your medical treatment and recovery.",
      icon: Stethoscope,
      tip: "Don't rush this phase. Your final settlement depends on reaching 'maximum medical improvement' (MMI)."
    },
    {
      number: 2,
      title: "Records Collection",
      duration: "1-2 months",
      description: "Once treatment stabilizes, I gather all medical records, bills, employment records, and other documentation to build your case.",
      icon: FileText,
      tip: "The more thorough the documentation, the stronger the case. This is where my experience pays off."
    },
    {
      number: 3,
      title: "Demand Letter",
      duration: "2-4 weeks to prepare",
      description: "I prepare a detailed demand package explaining your injuries, treatment, and damages. This goes to the insurance company with a specific dollar demand.",
      icon: Send,
      tip: "A well-documented demand letter often leads to better offers than informal negotiations."
    },
    {
      number: 4,
      title: "Negotiation",
      duration: "1-3 months typically",
      description: "Insurance companies rarely accept the first demand. There's usually back-and-forth negotiation. I handle all communication and counter-offers.",
      icon: Handshake,
      tip: "Insurance companies test your patience. Having an attorney signals you can wait for a fair offer."
    },
    {
      number: 5,
      title: "Settlement or Lawsuit",
      duration: "Varies",
      description: "Most cases settle during negotiation. If the insurance company won't offer fair compensation, we may file a lawsuit. Even then, most cases settle before trial.",
      icon: Scale,
      tip: "Having an attorney willing to go to trial often produces better settlement offers."
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="how-long-does-settlement-take-page">
      <SEOHead 
        title="How Long Does a Car Accident Settlement Take? | Amy C. Brown Law"
        description="Wondering how long your car accident settlement will take in Washington? Learn the typical timeline and what affects the process. Call (425) 675-1224."
        faqData={faqData}
        serviceData={{
          name: "Car Accident Settlement Process",
          description: "Guidance on the car accident settlement timeline in Washington State. Understanding each phase of the claims process.",
          category: "Personal Injury Law - Settlement Timeline"
        }}
      />

      {/* Hero Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Clock className="w-4 h-4" />
              Settlement Timeline
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary mb-6 leading-tight">
              How Long Does a Car Accident Settlement Take?
            </h1>
            
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              You're dealing with bills, you can't work, and you need to know: how long until this is resolved? I understand the anxiety. Let me explain what to expect.
            </p>

            <div className="bg-accent-soft rounded-xl p-6 mb-8 border-l-4 border-primary">
              <p className="text-slate-700 leading-relaxed">
                <strong>The honest answer:</strong> Most car accident settlements take 6 months to 2 years. The timeline depends on your injuries, treatment, and how reasonable the insurance company is. I know that's a wide range—let me break down why.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold text-lg hover:bg-secondary hover:text-white transition-all"
                data-testid="hero-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Visual */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              The 5 Phases of a Car Accident Case
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Here's what happens from accident to settlement. I'll keep you informed every step of the way.
            </p>
          </div>

          <div className="space-y-6">
            {phases.map((phase, index) => (
              <div key={phase.number} className="relative">
                <div className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-100">
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="bg-primary text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl">
                        {phase.number}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-3">
                        <phase.icon className="w-6 h-6 text-primary" />
                        <h3 className="font-heading text-xl font-semibold text-secondary">{phase.title}</h3>
                        <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                          {phase.duration}
                        </span>
                      </div>
                      <p className="text-slate-600 mb-4">{phase.description}</p>
                      <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                        <p className="text-amber-800 text-sm"><strong>Pro tip:</strong> {phase.tip}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {index < phases.length - 1 && (
                  <div className="hidden md:flex justify-center py-2">
                    <ArrowRight className="w-6 h-6 text-slate-300 rotate-90" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Affects Timeline */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-secondary mb-8 text-center">
            What Affects How Long Your Case Takes
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-heading font-semibold text-secondary mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-green-600" />
                Faster Resolution
              </h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                  Clear liability (other driver clearly at fault)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                  Minor injuries with quick recovery
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                  Good documentation from the start
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                  Reasonable insurance company
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                  Adequate insurance coverage
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-heading font-semibold text-secondary mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                Longer Timeline
              </h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />
                  Disputed liability (fault is contested)
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />
                  Serious injuries requiring extended treatment
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />
                  Surgery or ongoing medical care
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />
                  Insurance company delays or lowballs
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />
                  Need to file lawsuit and litigate
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Patience Pays */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="bg-accent-soft rounded-2xl p-8 border-l-4 border-primary">
            <h2 className="font-heading text-2xl font-bold text-secondary mb-4">
              Why Rushing Can Cost You
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              I know it's hard to wait. You have bills. You want this behind you. But here's what I've learned from years of handling these cases: settling too fast almost always means settling for less.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              Insurance companies know this. That's why they pressure people to settle quickly—before injuries fully develop, before you've completed treatment, before you understand the real impact on your life.
            </p>
            <p className="text-slate-700 leading-relaxed">
              My job is to protect you from that pressure. I'll keep your case moving efficiently, but I won't let you settle for less than you deserve just because the insurance company is impatient.
            </p>
          </div>
        </div>
      </section>

      {/* Communication Promise */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              You'll Never Be Left in the Dark
            </h2>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <p className="text-slate-700 leading-relaxed mb-6">
              One of the most common frustrations clients have with lawyers is the lack of updates. They hire an attorney, then hear nothing for months.
            </p>
            <p className="text-slate-700 leading-relaxed mb-6">
              That's not how I work. I'm committed to keeping you informed every step of the way. When something happens in your case, you'll know about it. When there's nothing happening, I'll tell you that too—and explain why.
            </p>
            <p className="text-slate-700 leading-relaxed">
              When you call or text, you reach me directly—not a paralegal or secretary. Your case matters, and you deserve to know what's happening with it.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Common Questions About Settlement Timelines
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl">
                <h3 className="font-heading font-semibold text-secondary mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Concepts - Entity SEO */}
      <section className="py-12 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Understanding the Legal Process
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="https://en.wikipedia.org/wiki/Settlement_(litigation)" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Legal Settlement <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Demand_letter" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Demand Letter <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Statute_of_limitations" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Statute of Limitations <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Negotiation" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Negotiation <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>
          <p className="text-xs text-slate-400 text-center mt-4">
            Learn more about these concepts on Wikipedia
          </p>
        </div>
      </section>

      {/* Related Car Accident Resources */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Related Car Accident Resources
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/car-crashes" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <Clock className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Car Accident Claims</p>
                <p className="text-sm text-slate-500">Overview of the car accident claim process</p>
              </div>
            </Link>
            <Link to="/how-much-is-my-case-worth" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <Scale className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Case Value Factors</p>
                <p className="text-sm text-slate-500">What determines your claim's worth</p>
              </div>
            </Link>
            <Link to="/should-i-accept-settlement" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <FileText className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Settlement Offers</p>
                <p className="text-sm text-slate-500">How to evaluate what they're offering</p>
              </div>
            </Link>
            <Link to="/everett-car-accident-lawyer" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <ArrowRight className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Everett Car Accident Lawyer</p>
                <p className="text-sm text-slate-500">Local representation in Snohomish County</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-6">
            Questions About Your Case Timeline?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            I'll give you a realistic expectation based on your specific situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowLongDoesSettlementTakePage;
